// src/app/services/subscription.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SubscriptionPrices } from '../_models/subscriptionPrices';
import { ApiService } from './api.service';
import { Payment, PaymentCollection } from '../_models/payment';
import { map } from 'rxjs/operators';
import { Card, CardCollection } from '../_models/card';
import { SUBSCRIPTION_BRANCHE, SUBSCRIPTION_GEEK, SUBSCRIPTION_VIP, SUBSCRIPTION_VIP_PIXEL, SUBSCRIPTION_VIP_PROMO, User } from '../_models';
import { AuthenticationService } from './authentication.service';
import { EventsService } from './events.service';

@Injectable({
    providedIn: 'root'
})
export class SubscriptionService {
    
    private payments: Array<Payment>;
    private cards: Array<Card>;
    
    private currentSubscriptionType: string;

    constructor(
        private http: HttpClient,
        private apiService: ApiService,
        private authenticationService: AuthenticationService,
        private eventsService: EventsService,
    ) {
        
    }

    /**
     * Init Monthly Payment
     */
    initMonthlyPayment(type:string, backURL:string, successURL:string, failedURL:string, origin:string): Observable<any> {
        let body = new FormData();
        body.append('type', type);
        body.append('backURL', backURL);
        body.append('successURL', successURL);
        body.append('failedURL', failedURL);
        body.append('origin', origin);
        return this.http.post<any>(`${environment.apiUrl}/subscribe`, body,
        {
            withCredentials: true
        });
    }

    /**
     * Init card auth
     */
    initCardAuth(backURL:string, successURL:string, failedURL:string, origin:string): Observable<any> {
        let body = new FormData();
        body.append('backURL', backURL);
        body.append('successURL', successURL);
        body.append('failedURL', failedURL);
        body.append('origin', origin);
        return this.http.post<any>(`${environment.apiUrl}/register-card`, body,
        {
            withCredentials: true
        });
    }

    /**
     * Init Flash Payment
     */
    initFlashPayment(type:string, backURL:string, successURL:string, failedURL:string, origin:string): Observable<any> {
        let body = new FormData();
        body.append('type', type);
        body.append('backURL', backURL);
        body.append('successURL', successURL);
        body.append('failedURL', failedURL);
        body.append('origin', origin);
        return this.http.post<any>(`${environment.apiUrl}/upgrade-subscription`, body,
        {
            withCredentials: true
        });
    }

    /**
     * Downgrade subscription
     */
    downgradeSubscription(type:string, origin:string): Observable<any> {
        let body = new FormData();
        body.append('type', type);
        body.append('origin', origin);
        return this.http.post<any>(`${environment.apiUrl}/downgrade-subscription`, body,
        {
            withCredentials: true
        });
    }

    /**
     * Cancel downgrade subscription
     */
    cancelDowngradeSubscription(origin:string): Observable<any> {
        let body = new FormData();
        body.append('origin', origin);
        return this.http.post<any>(`${environment.apiUrl}/cancel-downgrade-subscription`, body,
        {
            withCredentials: true
        });
    }

    /**
     * Downgrade subscription
     */
    cancelSubscription(origin:string): Observable<any> {
        let body = new FormData();
        body.append('origin', origin);
        return this.http.post<any>(`${environment.apiUrl}/cancel-subscription`, body,
        {
            withCredentials: true
        });
    }

    /**
     * Reactivate subscription
     */
    reactivateSubscription(origin:string): Observable<any> {
        let body = new FormData();
        body.append('origin', origin);
        return this.http.post<any>(`${environment.apiUrl}/reactivate-subscription`, body,
        {
            withCredentials: true
        });
    }

    /**
     * Calculate price delta between subscription
     */
    getPriceDelta(startSubsciptionType:string, targetSubsciptionType:string, nextDueDate:Date) {
        let startDate = new Date(nextDueDate);
        startDate.setMonth(startDate.getMonth() - 1);

        let now = new Date();

        const nowDue = now.valueOf() - nextDueDate.valueOf();
        const startDue = startDate.valueOf() - nextDueDate.valueOf();

        return (SubscriptionPrices.GEEK - SubscriptionPrices.BRANCHE) / startDue * nowDue;
    }

    /**
     * Get current user receipts
     */
    public getReceipts():Observable<{ items: Array<Payment>; totalItems: number}> {
        if (this.payments) {
            this.apiService.getReceipts().pipe(
                map((responseData: PaymentCollection) => {
                    let payments: Array<Payment> = [];
                    for (let payment of responseData['hydra:member']) {
                        payment['@id'] = payment['@id'].replace('/payments/', '');
                        payments.push(payment);
                    }
    
                    payments = payments.sort((a: Payment, b: Payment) => {
                        return (
                            new Date(b.updatedAt).getTime() -
                            new Date(a.updatedAt).getTime()
                        );
                    });
    
                    this.payments = payments;
                })
            ).subscribe();

            return new Observable(subscriber => {
                subscriber.next({
                    items: this.payments,
                    totalItems: this.payments.length
                });
            });
        } else {
            return this.apiService.getReceipts().pipe(
                map((responseData: PaymentCollection) => {
                    let payments: Array<Payment> = [];
                    for (let payment of responseData['hydra:member']) {
                        payment['@id'] = payment['@id'].replace('/payments/', '');
                        payments.push(payment);
    
                    }
    
                    payments = payments.sort((a: Payment, b: Payment) => {
                        return (
                            new Date(b.updatedAt).getTime() -
                            new Date(a.updatedAt).getTime()
                        );
                    });
    
                    this.payments = payments;
    
                    return {
                        items: this.payments,
                        totalItems: responseData['hydra:totalItems']
                    };
                })
            );
        }
    }

    /**
     * Get current user cards
     */
    public getCards():Observable<{ items: Array<Card>; totalItems: number}> {
        if (this.cards) {
            this.apiService.getCards().pipe(
                map((responseData: CardCollection) => {
                    let cards: Array<Card> = [];
                    for (let card of responseData['hydra:member']) {
                        card['@id'] = card['@id'].replace('/cards/', '');
                        cards.push(card);
                    }
    
                    cards = cards.sort((a: Card, b: Card) => {
                        return (
                            new Date(b.updatedAt).getTime() -
                            new Date(a.updatedAt).getTime()
                        );
                    });
    
                    this.cards = cards;
                })
            ).subscribe();

            return new Observable(subscriber => {
                subscriber.next({
                    items: this.cards,
                    totalItems: this.payments.length
                });
            });
        } else {
            return this.apiService.getCards().pipe(
                map((responseData: CardCollection) => {
                    let cards: Array<Card> = [];
                    for (let card of responseData['hydra:member']) {
                        card['@id'] = card['@id'].replace('/cards/', '');
                        cards.push(card);
                    }
    
                    cards = cards.sort((a: Card, b: Card) => {
                        return (
                            new Date(b.createdAt).getTime() -
                            new Date(a.createdAt).getTime()
                        );
                    });
    
                    this.cards = cards;
    
                    return {
                        items: this.cards,
                        totalItems: responseData['hydra:totalItems']
                    };
                })
            );
        }
    }

    /**
     * Set card active
     */
    setCardActive(cardId:string): Observable<any> {
        let body = new FormData();
        return this.http.put<any>(`${environment.apiUrl}/cards/${cardId}/activate`, body,
        {
            withCredentials: true
        });
    }

    /**
     * Manage current subscription
     */
    manageCurrentSubscription() {
        let storedData = localStorage.getItem("currentSubscription-"+this.authenticationService.meValue['@id']) ? JSON.parse(localStorage.getItem("currentSubscription-"+this.authenticationService.meValue['@id'])) : null;
        this.currentSubscriptionType = storedData ? storedData.type : null;

        if (this.currentSubscriptionType == SUBSCRIPTION_VIP_PROMO) {
            if (!this.authenticationService.isSubscribedVipPromo) {
                this.eventsService.openDowngradeVipPromoEvent.emit();
            }
        } else {
            if (this.authenticationService.isSubscribedVipPromo) {
                this.eventsService.openUpgradeVipPromoEvent.emit({subscribed: this.currentSubscriptionType != null, subscription: this.authenticationService.lastVipSubscription});
            }
        }

        if (this.currentSubscriptionType == SUBSCRIPTION_VIP_PIXEL) {
            if (!this.authenticationService.isSubscribedVipPixel) {
                this.eventsService.openDowngradeVipPixelEvent.emit();
            }
        } else {
            if (this.authenticationService.isSubscribedVipPixel) {
                this.eventsService.openUpgradeVipPixelEvent.emit({subscribed: this.currentSubscriptionType != null, subscription: this.authenticationService.lastVipSubscription});
            }
        }

        this.currentSubscriptionType = null;
        if (this.authenticationService.isSubscribedBranche) {
            this.currentSubscriptionType = SUBSCRIPTION_BRANCHE;
        }
        if (this.authenticationService.isSubscribedGeek) {
            this.currentSubscriptionType = SUBSCRIPTION_GEEK;
        }
        if (this.authenticationService.isSubscribedVip) {
            this.currentSubscriptionType = SUBSCRIPTION_VIP;
        }
        if (this.authenticationService.isSubscribedVipPixel) {
            this.currentSubscriptionType = SUBSCRIPTION_VIP_PIXEL;
        }
        if (this.authenticationService.isSubscribedVipPromo) {
            this.currentSubscriptionType = SUBSCRIPTION_VIP_PROMO;
        }

        localStorage.setItem('currentSubscription-'+this.authenticationService.meValue['@id'], JSON.stringify({
            type: this.currentSubscriptionType,
            date: new Date(),
            uid: this.authenticationService.meValue['@id']
        }));
    }

    /**
     * Start VIP Pixel
     */
    startVipPixelSubscription() {
        let body = new FormData();
        return this.http.post<any>(`${environment.apiUrl}/start-vip-pixel`, body, {
            withCredentials: true
        })
    }

    /**
     * Delete card
     */
    deleteCard(cardId:string): Observable<any> {
        let body = new FormData();
        return this.http.post<any>(`${environment.apiUrl}/cards/${cardId}/delete`, body,
        {
            withCredentials: true
        });
    }
}
